// StatisticsModule: API compatible, jStat-powered distributions, robust tests.
// Requires jStat (Node: npm i jstat). In browsers, ensure window.jStat is available.

(function (root, factory) {
    if (typeof module === 'object' && module.exports) {
        // CommonJS / Node
        let jStatLib = null;
        try {
            // jstat exports { jStat }, some builds export default; handle both
            const required = require('jstat');
            jStatLib = required.jStat || required.default || required;
        } catch (e) {
            // Allow constructing the class, but throw on first use if needed
            jStatLib = null;
        }
        module.exports = factory(jStatLib);
    } else {
        // Browser / UMD: expect window.jStat
        root.StatisticsModule = factory(root.jStat);
    }
}(typeof window.self !== 'undefined' ? window.self : this, function (jStat) {

class StatisticsModule {
    constructor(minSampleSize = 3) {
        this.minSampleSize = minSampleSize;

        // Kept for backward-compatibility with your original API (not used for decisions)
        this.criticalValues = {
            shapiroWilk: this._initShapiroWilkTable(),     // legacy, not used
            tDistribution: this._initTDistributionTable(), // legacy, not used
            normalDistribution: this._initNormalDistributionTable()
        };

        // Soft-check for jStat presence; we’ll guard calls and give clear errors.
        this._jStat = jStat || null;
    }

    // ------------------------------- Public API (unchanged) -------------------------------

    /**
     * Calculate statistics for a single group
     * @param {Object} data - { PRE: {Measure:[]}, POST: {Measure:[]} }
     * @returns {Object} results keyed by measure
     */
    singleGroupAnalysis(data) {
        this._ensureJStat(); // throw helpful error if missing
        const results = {};
        const measures = ['Dyspnoea', 'Pain', 'Worry', 'Sadness'];

        measures.forEach(measure => {
            const pre = this._sanitizeArray(data?.PRE?.[measure] ?? []);
            const post = this._sanitizeArray(data?.POST?.[measure] ?? []);

            const preMean = this.calculateMean(pre);
            const postMean = this.calculateMean(post);
            const diffMean = preMean - postMean;

            if (pre.length < this.minSampleSize || post.length < this.minSampleSize || pre.length !== post.length) {
                results[measure] = { preMean, postMean, prePostDifference: diffMean, pValue: '-' };
                return;
            }

            // Normality on paired differences
            const diffs = pre.map((x, i) => x - post[i]).filter(this._isFiniteNumber);
            let pValue;
            if (this.isNormallyDistributed(diffs) && diffs.length >= 10) {
                pValue = this.pairedTTest(pre, post); // uses accurate t CDF under the hood
            } else {
                pValue = this.wilcoxonSignedRankTest(pre, post); // robust non-param with tie correction
            }

            results[measure] = { preMean, postMean, prePostDifference: diffMean, pValue };
        });

        // Sleep: descriptive only (as in your original)
        const sleep = this._sanitizeArray(data?.PRE?.Sleep ?? []);
        results.Sleep = { mean: this.calculateMean(sleep), pValue: '-' };

        return results;
    }

    /**
     * Calculate statistics for comparing two groups
     * @param {Object} groupA - { PRE:{}, POST:{} }
     * @param {Object} groupB - { PRE:{}, POST:{} }
     * @returns {Object} results keyed by measure
     */
    twoGroupAnalysis(groupA, groupB) {
        this._ensureJStat();
        const results = {};
        const measures = ['Dyspnoea', 'Pain', 'Worry', 'Sadness'];

        measures.forEach(measure => {
            const preA  = this._sanitizeArray(groupA?.PRE?.[measure] ?? []);
            const postA = this._sanitizeArray(groupA?.POST?.[measure] ?? []);
            const preB  = this._sanitizeArray(groupB?.PRE?.[measure] ?? []);
            const postB = this._sanitizeArray(groupB?.POST?.[measure] ?? []);

            const preMean  = this.calculateMean([...preA, ...preB]);
            const postMean = this.calculateMean([...postA, ...postB]);
            const preDiff  = this.calculateMean(preA)  - this.calculateMean(preB);
            const postDiff = this.calculateMean(postA) - this.calculateMean(postB);
            const prePostDifference = preMean - postMean;

            const nA = Math.min(preA.length, postA.length);
            const nB = Math.min(preB.length, postB.length);

            if (nA < this.minSampleSize || nB < this.minSampleSize) {
                results[measure] = {
                    preMean, postMean, preDifference: preDiff, postDifference: postDiff,
                    prePostDifference, groupPValue: '-', timePValue: '-', interactionPValue: '-'
                };
                return;
            }

            // Interaction via deltas (POST - PRE), tested between groups
            const deltaA = Array.from({ length: nA }, (_, i) => postA[i] - preA[i]).filter(this._isFiniteNumber);
            const deltaB = Array.from({ length: nB }, (_, i) => postB[i] - preB[i]).filter(this._isFiniteNumber);

            let interactionP;
            if (this.isNormallyDistributed([...deltaA, ...deltaB]) && deltaA.length >= 10 && deltaB.length >= 10) {
                interactionP = this._welchTTest(deltaA, deltaB);
            } else {
                interactionP = this.mannWhitneyUTest(deltaA, deltaB);
            }

            // Time main effect: Δ pooled across groups vs 0 (one-sample)
            const deltasAll = [...deltaA, ...deltaB];
            const timeP = (this.isNormallyDistributed(deltasAll) && deltasAll.length >= 10)
                ? this._oneSampleTTest(deltasAll, 0)
                : this._signedRankOneSample(deltasAll, 0);

            // Group main effect at PRE (baseline difference)
            const groupBaselineP = (this.isNormallyDistributed([...preA, ...preB]) && preA.length >= 10 && preB.length >= 10)
                ? this._welchTTest(preA, preB)
                : this.mannWhitneyUTest(preA, preB);

            results[measure] = {
                preMean, postMean,
                preDifference: preDiff,
                postDifference: postDiff,
                prePostDifference,
                groupPValue: groupBaselineP,
                timePValue: timeP,
                interactionPValue: interactionP
            };
        });

        // Sleep: between-group baseline comparison
        const sleepA = this._sanitizeArray(groupA?.PRE?.Sleep ?? []);
        const sleepB = this._sanitizeArray(groupB?.PRE?.Sleep ?? []);
        const sleepMean = this.calculateMean([...sleepA, ...sleepB]);
        const sleepDiff = this.calculateMean(sleepA) - this.calculateMean(sleepB);

        if (sleepA.length >= this.minSampleSize && sleepB.length >= this.minSampleSize) {
            const pSleep = (this.isNormallyDistributed([...sleepA, ...sleepB]) && sleepA.length >= 10 && sleepB.length >= 10)
                ? this._welchTTest(sleepA, sleepB)
                : this.mannWhitneyUTest(sleepA, sleepB);

            results.Sleep = { mean: sleepMean, difference: sleepDiff, pValue: pSleep };
        } else {
            results.Sleep = { mean: sleepMean, difference: sleepDiff, pValue: '-' };
        }

        return results;
    }

    // ------------------------------- Public helpers (names kept) -------------------------------

    calculateMean(data) {
        const arr = this._sanitizeArray(data);
        return arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
    }

    /**
     * Normality check (API name preserved). Internally uses D’Agostino–Pearson K² with χ²(2) p-value via jStat.
     * Returns true if p > 0.05.
     */
    isNormallyDistributed(data) {
        this._ensureJStat();
        const x = this._sanitizeArray(data);
        const n = x.length;
        if (n < 8) return false; // DP test unreliable for tiny samples

        const mean = this.calculateMean(x);
        let m2 = 0, m3 = 0, m4 = 0;
        for (let i = 0; i < n; i++) {
            const d = x[i] - mean;
            const d2 = d * d;
            m2 += d2;
            m3 += d2 * d;
            m4 += d2 * d2;
        }
        m2 /= n; m3 /= n; m4 /= n;
        const g1 = m3 / Math.pow(m2, 1.5);       // skewness
        const g2 = m4 / (m2 * m2) - 3;          // excess kurtosis

        const Z1 = this._dagostinoZSkew(n, g1);
        const Z2 = this._dagostinoZKurt(n, g2);
        const K2 = Z1 * Z1 + Z2 * Z2;

        // p = 1 - CDF_chi2(K2; df=2)
        const p = 1 - this._chiSquareCDF(K2, 2);
        return p > 0.05;
    }

    // ------------------------------- Parametric tests (accurate p via jStat) -------------------------------

    pairedTTest(preData, postData) {
        this._ensureJStat();
        if (!Array.isArray(preData) || !Array.isArray(postData) || preData.length !== postData.length) return '-';
        const diffs = preData.map((pre, i) => pre - postData[i]).filter(this._isFiniteNumber);
        if (diffs.length < 2) return '-';
        const m = this.calculateMean(diffs);
        const sd = this._sd(diffs);
        if (sd === 0) return 1.0;
        const t = Math.abs(m / (sd / Math.sqrt(diffs.length)));
        const df = diffs.length - 1;
        return 2 * (1 - this._tCDF(t, df));
    }

    studentsTTest(sample1, sample2) {
        // Replaced with Welch’s t by default (safer). Keep method name for compatibility.
        return this._welchTTest(sample1, sample2);
    }

    // ------------------------------- Non-parametric tests -------------------------------

    wilcoxonSignedRankTest(preData, postData) {
        // Classic Wilcoxon signed-rank, excludes zero differences.
        const n = Math.min(preData?.length ?? 0, postData?.length ?? 0);
        if (n < 2) return '-';
        const diffs = Array.from({ length: n }, (_, i) => preData[i] - postData[i])
            .filter(d => Number.isFinite(d) && d !== 0);
        const m = diffs.length;
        if (m === 0) return 1.0;
        if (m === 1) return 1.0;

        // rank |diff| with average ranks + tie detection
        const items = diffs.map(d => ({ d, a: Math.abs(d) })).sort((u, v) => u.a - v.a);
        let rank = 1, hasTies = false;
        for (let i = 0; i < items.length; i++) {
            let j = i;
            while (j + 1 < items.length && items[j + 1].a === items[i].a) j++;
            if (j > i) hasTies = true;
            const avgRank = (rank + j + 1) / 2;
            for (let k = i; k <= j; k++) items[k].r = avgRank;
            rank = j + 2; i = j;
        }
        const Wplus = items.reduce((s, it) => s + (it.d > 0 ? it.r : 0), 0);
        const Wminus = (m * (m + 1)) / 2 - Wplus;
        const W = Math.min(Wplus, Wminus);

        if (!hasTies && m <= 25) {
            // exact (DP)
            const pmf = this._wilcoxonExactPMF(m);
            const cum = pmf.slice(0, Math.floor(W) + 1).reduce((s, p) => s + p, 0);
            return Math.min(1, 2 * cum);
        } else {
            // normal approx with tie correction + continuity
            const mean = m * (m + 1) / 4;
            let Tsum = 0, t = 1;
            for (let i = 1; i < items.length; i++) {
                if (items[i].a === items[i - 1].a) t++;
                else { if (t > 1) Tsum += (t ** 3 - t); t = 1; }
            }
            if (t > 1) Tsum += (t ** 3 - t);
            const varW = (m * (m + 1) * (2 * m + 1) - Tsum) / 24;
            if (varW <= 0) return 1.0;
            const z = (Math.abs(W - mean) - 0.5) / Math.sqrt(varW);
            return 2 * (1 - this._normalCDF(z));
        }
    }

    mannWhitneyUTest(sample1, sample2) {
        const x = this._sanitizeArray(sample1), y = this._sanitizeArray(sample2);
        const n1 = x.length, n2 = y.length;
        if (n1 < 1 || n2 < 1) return '-';

        const comb = x.map(v => ({ v, g: 1 })).concat(y.map(v => ({ v, g: 2 }))).sort((a, b) => a.v - b.v);
        let rank = 1, hasTies = false, ranks1 = [];
        for (let i = 0; i < comb.length; i++) {
            let j = i;
            while (j + 1 < comb.length && comb[j + 1].v === comb[i].v) j++;
            if (j > i) hasTies = true;
            const avgRank = (rank + j + 1) / 2;
            for (let k = i; k <= j; k++) if (comb[k].g === 1) ranks1.push(avgRank);
            rank = j + 2; i = j;
        }
        const R1 = ranks1.reduce((s, r) => s + r, 0);
        const U1 = R1 - (n1 * (n1 + 1)) / 2;
        const U2 = n1 * n2 - U1;
        const U  = Math.min(U1, U2);

        if (!hasTies && n1 <= 20 && n2 <= 20) {
            const pmf = this._mannWhitneyExactPMF(n1, n2);
            const cum = pmf.slice(0, Math.floor(U) + 1).reduce((s, p) => s + p, 0);
            return Math.min(1, 2 * cum);
        } else {
            const n = n1 + n2;
            // tie correction
            let Tsum = 0, tcount = 1;
            for (let i = 1; i < comb.length; i++) {
                if (comb[i].v === comb[i - 1].v) tcount++;
                else { if (tcount > 1) Tsum += (tcount ** 3 - tcount); tcount = 1; }
            }
            if (tcount > 1) Tsum += (tcount ** 3 - tcount);

            const varU = n1 * n2 * (n * (n + 1) - Tsum / (n - 1)) / 12;
            if (!Number.isFinite(varU) || varU <= 0) return 1.0;

            const meanU = n1 * n2 / 2;
            const cc = (U - meanU) === 0 ? 0 : 0.5 * Math.sign(U - meanU);
            const z = (Math.abs(U - meanU) - cc) / Math.sqrt(varU);
            return 2 * (1 - this._normalCDF(z));
        }
    }

    // ------------------------------- Internals: additional tests used by twoGroupAnalysis -------------------------------

    _oneSampleTTest(sample, mu0 = 0) {
        this._ensureJStat();
        const x = this._sanitizeArray(sample);
        const n = x.length;
        if (n < 2) return '-';
        const m = this.calculateMean(x);
        const sd = this._sd(x);
        if (sd === 0) return 1.0;
        const t = Math.abs((m - mu0) / (sd / Math.sqrt(n)));
        return 2 * (1 - this._tCDF(t, n - 1));
    }

    _signedRankOneSample(deltas, mu0 = 0) {
        const diffs = this._sanitizeArray(deltas.map(v => v - mu0)).filter(d => d !== 0);
        if (diffs.length < 2) return '-';
        // Reuse Wilcoxon by comparing to 0
        return this.wilcoxonSignedRankTest(diffs, new Array(diffs.length).fill(0));
    }

    _welchTTest(a, b) {
        this._ensureJStat();
        const x = this._sanitizeArray(a), y = this._sanitizeArray(b);
        const n1 = x.length, n2 = y.length;
        if (n1 < 2 || n2 < 2) return '-';
        const m1 = this.calculateMean(x), m2 = this.calculateMean(y);
        const v1 = this._variance(x), v2 = this._variance(y);
        const se2 = v1 / n1 + v2 / n2;
        if (se2 <= 0) return 1.0;
        const t = Math.abs((m1 - m2) / Math.sqrt(se2));
        const df = (se2 ** 2) / ((v1 * v1) / (n1 * n1 * (n1 - 1)) + (v2 * v2) / (n2 * n2 * (n2 - 1)));
        return 2 * (1 - this._tCDF(t, df));
    }

    // ------------------------------- Descriptive helpers -------------------------------

    _sanitizeArray(arr) {
        if (!Array.isArray(arr)) return [];
        return arr.map(Number).filter(this._isFiniteNumber);
    }
    _isFiniteNumber(x) { return Number.isFinite(x); }
    _variance(arr) {
        const n = arr.length;
        if (n < 2) return 0;
        const m = this.calculateMean(arr);
        let s = 0;
        for (let i = 0; i < n; i++) { const d = arr[i] - m; s += d * d; }
        return s / (n - 1);
    }
    _sd(arr) { return Math.sqrt(this._variance(arr)); }

    // ------------------------------- Normality: D’Agostino–Pearson transforms -------------------------------

    _dagostinoZSkew(n, g1) {
        // Approx per D’Agostino (1970, 1990)
        const y = g1 * Math.sqrt((n + 1) * (n + 3) / (6 * (n - 2)));
        const beta2 = 3 * (n*n + 27*n - 70) * (n + 1) * (n + 3) / ((n - 2) * (n + 5) * (n + 7) * (n + 9));
        const W2 = -1 + Math.sqrt(2 * (beta2 - 1));
        const delta = 1 / Math.sqrt(Math.log(Math.sqrt(W2)));
        const alpha = Math.sqrt(2 / (W2 - 1));
        return alpha * Math.log(y / delta + Math.sqrt(1 + (y / delta) ** 2));
    }

    _dagostinoZKurt(n, g2) {
        const E = 3 * (n - 1) / (n + 1);
        const Var = (24 * n * (n - 2) * (n - 3)) / ((n + 1) ** 2 * (n + 3) * (n + 5));
        const X = (g2 - E) / Math.sqrt(Var);
        const B = (6 * (n*n - 5*n + 2)) / ((n + 7) * (n + 9)) * Math.sqrt(6 * (n + 3) * (n + 5) / (n * (n - 2) * (n - 3)));
        const A = 6 + (8 / B) * (2 / B + Math.sqrt(1 + 4 / (B * B)));
        // Wilson-Hilferty transform-ish for kurtosis component
        return (1 - 2 / (9 * A) - Math.cbrt(1 - 2 / A) * X / Math.sqrt(2 / (9 * A))) / Math.sqrt(2 / (9 * A));
    }

    // ------------------------------- Exact PMFs (DP) -------------------------------

    _wilcoxonExactPMF(n) {
        const S = n * (n + 1) / 2;
        const ways = new Array(S + 1).fill(0);
        ways[0] = 1;
        for (let r = 1; r <= n; r++) {
            for (let s = S; s >= r; s--) ways[s] += ways[s - r];
        }
        const total = 2 ** n;
        return ways.map(c => c / total);
    }

    _mannWhitneyExactPMF(n1, n2) {
        const maxU = n1 * n2;
        const ways = Array.from({ length: n1 + 1 }, () =>
            Array.from({ length: n2 + 1 }, () => new Array(maxU + 1).fill(0))
        );
        ways[0][0][0] = 1;
        for (let i = 0; i <= n1; i++) {
            for (let j = 0; j <= n2; j++) {
                for (let u = 0; u <= maxU; u++) {
                    const w = ways[i][j][u];
                    if (!w) continue;
                    if (i < n1) ways[i + 1][j][u + j] += w; // adding group1 contributes j
                    if (j < n2) ways[i][j + 1][u] += w;      // group2 contributes 0
                }
            }
        }
        const totalWays = ways[n1][n2].reduce((s, c) => s + c, 0);
        return ways[n1][n2].map(c => c / totalWays);
    }

    // ------------------------------- Distributions via jStat -------------------------------

    _ensureJStat() {
        if (!this._jStat) {
            throw new Error(
                "jStat is required but not found. Install with `npm i jstat` (Node) " +
                "or include it in the browser (window.jStat)."
            );
        }
    }

    _normalCDF(z) {
        // jStat.normal.cdf(x, mean, sd)
        return this._jStat.normal.cdf(z, 0, 1);
    }

    _tCDF(t, df) {
        // Return CDF at |t| to be used for two-tailed p = 2*(1 - CDF(|t|))
        if (!(df > 0)) return NaN;
        const x = Math.abs(t);
        return this._jStat.studentt.cdf(x, df);
    }

    _chiSquareCDF(x, k) {
        return this._jStat.chisquare.cdf(x, k);
    }

    // ------------------------------- Legacy tables (unused, retained for API) -------------------------------

    _initShapiroWilkTable() { return {}; }
    _initTDistributionTable() { return {}; }
    _initNormalDistributionTable() {
        return { 0.05: 1.96, 0.01: 2.576, 0.001: 3.291 };
    }
}

return StatisticsModule;

}));
